import { useState, useEffect, useCallback } from "react";
import { LocationData } from "../components/DefaultLocations";
import commonLocations from "../data/commonLocations";
import { geocodeLocation } from "../utils/geocoding";
import { fetchSunriseSunsetData } from "../utils/sunriseSunsetApi";
import {
  getCustomShortcuts,
  removeCustomShortcut,
  CustomShortcut,
} from "../utils/customShortcutsStorage";

export const useDefaultLocations = () => {
  const [defaultLocations, setDefaultLocations] = useState<LocationData[]>([
    { name: "București", sunrise: null },
    { name: "Cluj-Napoca", sunrise: null },
    { name: "Brașov", sunrise: null },
    { name: "Iasi", sunrise: null },
    { name: "Constanța", sunrise: null },
    { name: "Costinești", sunrise: null },
  ]);
  const [customShortcuts, setCustomShortcuts] = useState<CustomShortcut[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Load custom shortcuts from localStorage
  useEffect(() => {
    const shortcuts = getCustomShortcuts();
    setCustomShortcuts(shortcuts);
  }, []);

  // Pre-load coordinates for default locations but don't fetch sunrise times yet
  useEffect(() => {
    const prepareDefaultLocations = async () => {
      if (initialized) return; // Don't re-initialize if already done

      const updatedLocations = [...defaultLocations];

      for (let i = 0; i < updatedLocations.length; i++) {
        const locationName = updatedLocations[i].name;
        // Just get coordinates from local database
        const normalizedName = locationName.toLowerCase().trim().split(",")[0];

        if (commonLocations[normalizedName]) {
          updatedLocations[i].lat = commonLocations[normalizedName].lat;
          updatedLocations[i].lng = commonLocations[normalizedName].lng;

          // Set API display names for Brașov
          if (normalizedName === "brașov" || normalizedName === "brasov") {
            updatedLocations[i].apiDisplayName = "Brasov, Zona Metropolitană Brașov, Brașov, Romania";
          } else if (normalizedName === "cluj-napoca" || normalizedName === "cluj") {
            updatedLocations[i].apiDisplayName = "Cluj-Napoca, Cluj Metropolitan Area, Cluj, Romania";
          } else if (normalizedName === "iasi" || normalizedName === "iași") {
            updatedLocations[i].apiDisplayName = "Iași, Iași Metropolitan Area, Iași, Romania";
          } else if (normalizedName === "bucurești") {
            updatedLocations[i].apiDisplayName = "Bucharest, Romania";
          } else if (normalizedName === "constanta" || normalizedName === "constanța") {
            updatedLocations[i].apiDisplayName = "Constanța, Constanta Metropolitan Area, Constanța, Romania";
          } else if (normalizedName === "costinesti" || normalizedName === "costinești") {
            updatedLocations[i].apiDisplayName = "Costinești, Constanta Metropolitan Area, Constanța, Romania";
          }
        } else {
          // If not found in local database, try to geocode it
          const geoResult = await geocodeLocation(locationName);
          if (geoResult) {
            updatedLocations[i].lat = geoResult.lat;
            updatedLocations[i].lng = geoResult.lng;
          }
        }
      }

      setDefaultLocations(updatedLocations);
      setInitialized(true);
    };

    prepareDefaultLocations();
  }, [defaultLocations, initialized]); // Only re-run if initialized changes

  const fetchLocationSunrise = useCallback(
    async (locationName: string, index: number) => {
      try {
        const geoResult = await geocodeLocation(locationName);
        if (geoResult) {
          const sunriseData = await fetchSunriseSunsetData(
            geoResult.lat,
            geoResult.lng
          );

          if (sunriseData) {
            setDefaultLocations((currentLocations) => {
              const updatedLocations = [...currentLocations];
              updatedLocations[index].sunrise = sunriseData.sunrise;
              updatedLocations[index].lat = geoResult.lat;
              updatedLocations[index].lng = geoResult.lng;
              return updatedLocations;
            });
          }
        }
      } catch (error) {
        console.error(`Error fetching sunrise for ${locationName}:`, error);
      }
    },
    []
  );

  const handleRemoveCustomShortcut = useCallback((name: string) => {
    removeCustomShortcut(name);
    const updatedShortcuts = getCustomShortcuts();
    setCustomShortcuts(updatedShortcuts);
  }, []);

  const refreshCustomShortcuts = useCallback(() => {
    const shortcuts = getCustomShortcuts();
    setCustomShortcuts(shortcuts);
  }, []);

  return {
    defaultLocations,
    customShortcuts,
    fetchLocationSunrise,
    handleRemoveCustomShortcut,
    refreshCustomShortcuts,
    initialized,
  };
};
